<template>
  <div v-if="homeLayout">
    <v-breadcrumbs
      v-if="itemsList.length > 1"
      class="pa-0 flex-wrap breadcrumbs-home"
    >
      <template v-for="(item, i) in itemsList.slice(0, -1)" :key="`item-${i}`">
        <v-breadcrumbs-item
          :class="item.id !== (paginatedDocumentsCurrentFolderId || currentProjectsFolder.id) ? 'clickable' : ''"
          class="px-0"
          @click="handleGoTo(item)"
        >
          <span class="text-text-secondary font-weight-regular text--x-small">{{ item.name }}</span>
        </v-breadcrumbs-item>

        <v-breadcrumbs-divider v-if="i < itemsList.length - 2">
          <app-material-icon name="keyboard_arrow_right" />
        </v-breadcrumbs-divider>
      </template>
    </v-breadcrumbs>

    <div
      v-if="itemsList.length"
      class="font-weight-bold text-text-secondary mt-1"
      style="font-size: 24px;"
    >
      {{ itemsList[itemsList.length - 1].name }}
    </div>
  </div>

  <div v-else>
    <div ref="container" class="d-flex align-center overflow-hidden flex-nowrap min-w-0">
      <!-- Always show first (root) item if it exists -->
      <template v-if="itemsList.length">
        <AppDroppableArea
          :disabled="isFolderDropDisabled(itemsList[0], 0)"
          @dragenter="dragEnterParentFolder(itemsList[0], 0)"
          @dragleave="dragLeaveParentFolder"
          @ondrop="dropDocumentsInParentFolder($event, itemsList[0])"
        >
          <span
            class="py-1 px-3"
            :class="[
              itemsList[0].id !== (paginatedDocumentsCurrentFolderId || currentProjectsFolder.id) ? 'clickable' : '',
              'text-text-secondary',
            ]"
            @click="handleGoTo(itemsList[0])"
          >
            <span :class="itemsList.length === 1 ? 'font-weight-bold' : ''">
              <span v-if="numberingEnabled && itemsList[0].numbering" class="pr-1">{{ itemsList[0].numbering }}</span>
              <span>{{ itemsList[0].name }}</span>
            </span>
          </span>
        </AppDroppableArea>
        <v-breadcrumbs-divider v-if="itemsList.length > 1">
          <app-material-icon name="keyboard_arrow_right" />
        </v-breadcrumbs-divider>
      </template>

      <template v-if="hiddenFolders.length">
        <v-menu offset-y>
          <template #activator="{ props }">
            <span v-bind="props" data-locator="ellipsis-menu" class="ellipsis-menu clickable text--bold text-text-secondary py-1 px-2">...</span>
          </template>
          <v-list>
            <v-list-item v-for="(item, i) in hiddenFolders" :key="'hidden-'+i" @click="handleGoTo(item)">
              <v-list-item-title class="text-text-secondary">
                <span v-if="numberingEnabled && item.numbering" class="pr-1">{{ item.numbering }}</span>
                <span>{{ item.name }}</span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-breadcrumbs-divider>
          <app-material-icon name="keyboard_arrow_right" />
        </v-breadcrumbs-divider>
      </template>

      <!-- Visible folders (excluding first and last) -->
      <template v-for="(item, i) in visibleFolders" :key="'visible-' + i">
        <AppDroppableArea
          :disabled="isFolderDropDisabled(item, i)"
          @dragenter="dragEnterParentFolder(item, i)"
          @dragleave="dragLeaveParentFolder"
          @ondrop="dropDocumentsInParentFolder($event, item)"
        >
          <span
            class="py-1 px-3 clickable text-text-secondary"
            @click="handleGoTo(item)"
          >
            <span v-if="numberingEnabled && item.numbering" class="pr-1">{{ item.numbering }}</span>
            <span>{{ item.name }}</span>
          </span>
        </AppDroppableArea>
        <v-breadcrumbs-divider v-if="i < visibleFolders.length - 1 || (lastFolder && lastFolder !== itemsList[0])">
          <app-material-icon name="keyboard_arrow_right" />
        </v-breadcrumbs-divider>
      </template>

      <template v-if="lastFolder && lastFolder !== itemsList[0]">
        <AppDroppableArea
          :disabled="isFolderDropDisabled(lastFolder, itemsList.length - 1)"
          class="overflow-hidden"
          @dragenter="dragEnterParentFolder(lastFolder, itemsList.length - 1)"
          @dragleave="dragLeaveParentFolder"
          @ondrop="dropDocumentsInParentFolder($event, lastFolder)"
        >
          <AppTooltip
            :max-width="TOOLTIP_MAX_WIDTH"
            location="bottom"
          >
          <template #activator="{ props }">
              <span
                class="py-1 px-3 w-100 d-flex"
                v-bind="props"
              >
            <span class="text-truncate font-weight-bold text-text-secondary">
              <span v-if="numberingEnabled && lastFolder.numbering" class="pr-1">{{ lastFolder.numbering }}</span>
              <span>{{ lastFolder.name }}</span>
            </span>
            </span>
            </template>
            <span v-if="numberingEnabled && lastFolder.numbering" class="pr-1">{{ lastFolder.numbering }}</span>
            <span>{{ lastFolder.name }}</span>
          </AppTooltip>
        </AppDroppableArea>
      </template>

      <!-- Hidden measurement row -->
      <span ref="measureRow" style="position: absolute; left: -9999px; top: -9999px; visibility: hidden; white-space: nowrap;">
        <template v-for="(item, i) in itemsList" :key="'measure-'+i">
          <span v-if="i > 0" class="mx-2"><app-material-icon name="keyboard_arrow_right" /></span>
          <span :ref="el => setMeasureFolderRef(el, i)" class="breadcrumb-measure-item py-1 px-3">
            <span v-if="numberingEnabled && item.numbering" class="pr-1">{{ item.numbering }}</span>
            <span>{{ item.name }}</span>
          </span>
        </template>
      </span>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

import AppDroppableArea from '@/common/AppDroppableArea.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { flatten } from '@/common/utils/flatArray'
import { toast } from '@/common/utils/toast'
import { FileToUpload, FolderToUpload } from '@/models/documentToUpload.model'
import { FOLDER_ROUTE_NAME, ROOMS_ROUTE_NAME } from '@/router/routerData'
import { AppUploadHandlerMutationTypes } from '@/store/modules/app-upload-handler/mutation_types'
import { DESELECT_ALL_DOCUMENTS, LOAD_FOLDERS_TREE, MOVE_FILDERS } from '@/store/modules/documents/action_types'
import { SET_MOVEMENT_SNACKBAR_IS_OPEN } from '@/store/modules/documents/mutation_types'
import { DocumentsBreadcrumbActionTypes } from '@/store/modules/documents-breadcrumb/action_types'
import { START_UPLOAD } from '@/store/modules/documents-upload-files/action_types'
import { ADD_FILES } from '@/store/modules/documents-upload-files/mutation_types'

const TOOLTIP_MAX_WIDTH = 600

export default {
  name: 'DocumentsBreadcrumb',
  components: {
    AppDroppableArea,
    AppTooltip,
  },
  props: {
    items: {
      type: Array,
      default: () => ([]),
    },
  },
  data () {
    return {
      draggedFolder: null,
      FOLDER_ROUTE_NAME,
      ROOMS_ROUTE_NAME,
      visibleFolders: [],
      hiddenFolders: [],
      measureFolderRefs: [],
      resizeObserver: null,
      TOOLTIP_MAX_WIDTH,
    }
  },
  computed: {
    ...mapState('global', ['homeLayout']),
    ...mapState('projectsFolders', ['currentProjectsFolder']),
    ...mapGetters('documents', ['isRootFolder']),
    ...mapState('documents', ['documentsToMove', 'foldersTree', 'numberingEnabled', 'paginatedDocumentsCurrentFolderId']),
    ...mapState('documentsBreadcrumb', ['breadcrumb']),
    ...mapGetters('room', ['currentUserRights', 'roomMnemo']),
    ...mapGetters('documentsBreadcrumb', ['hasTodoFolderOrChild', 'hasQaFolderOrChild']),
    itemsList () {
      return this.homeLayout ? this.items : this.breadcrumb
    },
    lastFolder () {
      return this.itemsList && this.itemsList.length ? this.itemsList[this.itemsList.length - 1] : null
    },
  },
  watch: {
    breadcrumb: {
      handler () {
        this.DESELECT_ALL_DOCUMENTS()
        this.calculateVisibleFolders()
      },
      deep: true,
    },
    draggedFolder (folder) {
      if (folder) {
        this.openUploadHint(folder.name)
      } else {
        this.closeUploadHint()
      }
    },
  },
  mounted() {
    if (!this.homeLayout && this.$refs.container) {
      this.resizeObserver = new window.ResizeObserver(() => {
        this.calculateVisibleFolders()
      })
      this.resizeObserver.observe(this.$refs.container)
    }
  },
  beforeUnmount () {
    if (this.resizeObserver && this.$refs.container) {
      this.resizeObserver.unobserve(this.$refs.container)
      this.resizeObserver.disconnect()
    }
    this.RESET_BREADCRUMB()
  },
  methods: {
    setMeasureFolderRef(el, i) {
      if (el) {
        this.measureFolderRefs[i] = el
      }
    },
    async calculateVisibleFolders() {
      if (this.homeLayout) {
        return
      }

      this.measureFolderRefs = []
      await this.$nextTick()
      const container = this.$refs.container
      const measureRow = this.$refs.measureRow
      if (!container || !measureRow || !this.itemsList || !this.itemsList.length) {
        return
      }

      const containerWidth = container.offsetWidth
      const ellipsisWidth = 50
      const dividerWidth = 30
      const items = this.itemsList

      // If only 1 or 2 items, no middle items to show
      if (items.length <= 2) {
        this.visibleFolders = []
        this.hiddenFolders = []
        return
      }

      // Get middle items (excluding first and last)
      const middleItems = items.slice(1, -1)
      if (middleItems.length === 0) {
        this.visibleFolders = []
        this.hiddenFolders = []
        return
      }

      // Calculate mandatory space (root + last + their dividers)
      const rootEl = this.measureFolderRefs[0]
      const lastEl = this.measureFolderRefs[items.length - 1]
      let mandatoryWidth = 0

      if (rootEl) mandatoryWidth += rootEl.offsetWidth + dividerWidth
      if (lastEl) mandatoryWidth += lastEl.offsetWidth

      // Try to fit all middle items without ellipsis first
      let totalWidthWithoutEllipsis = mandatoryWidth
      for (let i = 0; i < middleItems.length; i++) {
        const itemIndex = i + 1 // Adjust for slice offset
        const el = this.measureFolderRefs[itemIndex]
        if (el) {
          totalWidthWithoutEllipsis += el.offsetWidth + dividerWidth
        }
      }

      if (totalWidthWithoutEllipsis <= containerWidth) {
        // All items fit without ellipsis
        this.visibleFolders = middleItems
        this.hiddenFolders = []
        return
      }

      // Need ellipsis - prioritize items closest to current folder for better context
      let currentWidth = mandatoryWidth + ellipsisWidth + dividerWidth
      let visible = []
      let hidden = []

      // Start from the _right_ (closest to current folder) and work backwards
      // This ensures we show the most relevant context
      for (let i = middleItems.length - 1; i >= 0; i--) {
        const itemIndex = i + 1
        const el = this.measureFolderRefs[itemIndex]
        if (!el) break

        const itemWidth = el.offsetWidth + dividerWidth

        if (currentWidth + itemWidth <= containerWidth) {
          // Add to beginning of visible array to maintain correct order
          visible.unshift(middleItems[i])
          currentWidth += itemWidth
        } else {
          // Can't fit this item - add all items from start to here to hidden
          hidden = middleItems.slice(0, i + 1)
          break
        }
      }

      this.visibleFolders = visible
      this.hiddenFolders = hidden
    },
    ...mapActions('documentsBreadcrumb', [DocumentsBreadcrumbActionTypes.GOTO, DocumentsBreadcrumbActionTypes.RESET_BREADCRUMB]),
    ...mapActions('documents', [DESELECT_ALL_DOCUMENTS, LOAD_FOLDERS_TREE]),
    ...mapMutations('appUploadHandler', [AppUploadHandlerMutationTypes.SET_UPLOAD_HINT]),
    ...mapActions('documents', [MOVE_FILDERS]),
    ...mapActions('documentsUploadFiles', [START_UPLOAD]),
    ...mapMutations('documentsUploadFiles', [ADD_FILES]),
    ...mapMutations('documents', [SET_MOVEMENT_SNACKBAR_IS_OPEN]),
    handleGoTo (item) {
      if (this.homeLayout && item.id !== this.currentProjectsFolder.id) {
        this.navigateToProjectsFolder(item)
      } else if (!this.homeLayout && item.id !== this.paginatedDocumentsCurrentFolderId) {
        this.GOTO(item)
      }
    },
    navigateToProjectsFolder (item) {
      if (item.id) {
        this.$router.push({ name: FOLDER_ROUTE_NAME, params: { folderId: item.id } })
      } else {
        this.$router.push({ name: ROOMS_ROUTE_NAME })
      }
    },
    dragEnterParentFolder (folder, index) {
      if (this.homeLayout) return
      if (!this.isFolderDropDisabled(folder, index)) {
        this.draggedFolder = folder
      }
    },
    dragLeaveParentFolder () {
      if (this.homeLayout) return
      this.draggedFolder = null
    },
    dropDocumentsInParentFolder ($event, destinationFolder) {
      const documentFromClosd = $event.dataTransfer.getData('fromClosd') === 'true'
      if (documentFromClosd) {
        const documentId = Number($event.dataTransfer.getData('documentId'))
        const documentType = $event.dataTransfer.getData('documentType')
        const dropFolderInItself = documentId === destinationFolder.id && documentType === destinationFolder.type

        if (!dropFolderInItself) {
          this.moveClosdDocument(destinationFolder)
        }
      } else {
        this.dropLocalDocuments($event, destinationFolder)
      }
    },
    openUploadHint (destinationFolder) {
      this.SET_UPLOAD_HINT({
        visible: true,
        destinationFolder: destinationFolder,
      })
    },
    closeUploadHint () {
      this.SET_UPLOAD_HINT({ visible: false })
    },
    async moveClosdDocument (targetFolder) {
      try {
        await this.MOVE_FILDERS({
          documents: this.documentsToMove.documents,
          targetFolderId: targetFolder.id,
        })
        this.SET_MOVEMENT_SNACKBAR_IS_OPEN(true)
      } catch {
        toast.error(this.$t('project.documents.DocumentsBreadcrumb.moveError'))
      }

      if (this.documentsToMove.documents.some(doc => doc.type === 'folder')) {
        await this.LOAD_FOLDERS_TREE()
      }
      if (this.documentsToMove.isDragElementSelected) {
        this.DESELECT_ALL_DOCUMENTS()
      }
    },
    async dropLocalDocuments ($event, targetFolder) {
      const dataTransferItemList = $event.dataTransfer ? $event.dataTransfer.items : []
      const documentsToUpload = []
      const queue = []
      dataTransferItemList.forEach(item => {
        queue.push(item.webkitGetAsEntry())
      })

      for (const entry of queue) {
        if (entry.isFile) {
          const fileToUpload = await this.generateFileToUpload(entry, targetFolder.id)
          documentsToUpload.push(fileToUpload)
        }

        if (entry.isDirectory) {
          if (!this.currentUserRights.canCreateFolders) {
            toast.error(this.$t('project.documents.DocumentsBreadcrumb.noFolderAsGuest'))
            return
          }
          if (this.hasTodoFolderOrChild) {
            toast.error(this.$t('project.documents.DocumentsBreadcrumb.noFolderInChecklist'))
            return
          }
          if (this.hasQaFolderOrChild) {
            toast.error(this.$t('project.documents.DocumentsBreadcrumb.noFolderInQa'))
            return
          }

          const folderToUpload = await this.generateFolderToUpload(entry, targetFolder.id)
          documentsToUpload.push(folderToUpload)
        }
      }

      this.ADD_FILES(documentsToUpload)
      this.START_UPLOAD()
    },
    async generateFolderToUpload (folder, parentFolderId) {
      const folderToUpload = new FolderToUpload(folder.name, [], parentFolderId)

      let readEntries
      try {
        readEntries = await new Promise((resolve, reject) => {
          folder.createReader().readEntries(resolve, reject)
        })
      } catch (err) {
        console.error(err)
      }

      for (const entry of readEntries) {
        if (entry.isFile) {
          folderToUpload.children.push(await this.generateFileToUpload(entry))
          continue
        }

        if (entry.isDirectory) {
          folderToUpload.children.push(await this.generateFolderToUpload(entry))
        }
      }

      return folderToUpload
    },
    async generateFileToUpload (file, parentFolderId) {
      let entryFile
      try {
        entryFile = await new Promise((resolve, reject) => {
          file.file(resolve, reject)
        })
      } catch (err) {
        console.error(err)
      }

      return new FileToUpload(entryFile, 'document', parentFolderId)
    },
    isFolderDropDisabled (folder, index) {
      if (this.documentsToMove.isDragFromFoldersTree && !this.canDropFromFolderTree(folder)) {
        return true
      }
      return index === this.breadcrumb.length - 1 || folder.hasOwnProperty('specialFolder')
    },
    canDropFromFolderTree (targetFolder) {
      if (!targetFolder || !this.documentsToMove.isDragFromFoldersTree) {
        return false
      }
      const flattenFolders = flatten([], this.foldersTree?.folders)
      const matchingFolder = flattenFolders.find(flattenFolder => flattenFolder.id === this.documentsToMove.documents[0].id)
      const childrenOfMatchingFolder = flatten([], matchingFolder?.children)
      const dropInSameParent = this.documentsToMove.originalFolderId === targetFolder.id
      const dropToSameFolder = this.documentsToMove.documents[0].id === targetFolder.id
      const dropInChildrenFolder = childrenOfMatchingFolder.some(childFolder => childFolder.id === targetFolder.id)
      return !dropInSameParent && !dropToSameFolder && !dropInChildrenFolder
    },
  },
}
</script>

<style scoped>
.ellipsis-menu {
  user-select: none;
}

.ellipsis-menu:hover, .clickable:hover {
  background-color: rgba(var(--v-theme-surface-hover));
  border-radius: 4px;
}
</style>
